import React from 'react'
import type { GamingSpendingProps } from '@/features/enrollment'

export default function GamingSpending({ enrollmentData, updateEnrollmentData, errors, originalMonthlySpending, monthlySpendingAutoSelected, setMonthlySpendingAutoSelected, validateMonthlySpending }: GamingSpendingProps) {
  return (
    <>
      {/* Cloud Gaming Subscription */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Do you subscribe to any cloud/internet gaming subscription(s) (online gaming subscriptions) <span className="text-red-500">*</span>
        </label>
        
        {/* Auto-selection indicator */}
        {enrollmentData.cloudGamingSubscription === 'Yes' && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Auto-selected:</span>
              <span className="ml-2 text-blue-600 font-semibold">Yes</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              You can change this selection using the options below if needed.
            </p>
          </div>
        )}
        
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="cloudGamingSubscription" 
              value="Yes" 
              checked={enrollmentData.cloudGamingSubscription === 'Yes'} 
              onChange={() => updateEnrollmentData('cloudGamingSubscription', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="cloudGamingSubscription" 
              value="No" 
              checked={enrollmentData.cloudGamingSubscription === 'No'} 
              onChange={() => updateEnrollmentData('cloudGamingSubscription', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="cloudGamingSubscription" 
              value="Unsure" 
              checked={enrollmentData.cloudGamingSubscription === 'Unsure'} 
              onChange={() => updateEnrollmentData('cloudGamingSubscription', 'Unsure')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Unsure</span>
          </label>
        </div>
        {errors.cloudGamingSubscription && (
          <p className="mt-1 text-sm text-red-600">{errors.cloudGamingSubscription}</p>
        )}
      </div>

      {/* VR Accessories */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Do you use virtual reality headsets, gloves, or other video game accessories? <span className="text-red-500">*</span>
        </label>
        
        {/* Auto-selection indicator */}
        {(enrollmentData.vrAccessories === 'Yes' || enrollmentData.vrAccessories === 'No') && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Auto-selected:</span>
              <span className="ml-2 text-blue-600 font-semibold">{enrollmentData.vrAccessories}</span>
              <span className="ml-2 text-xs text-blue-600">(90% chance for Yes)</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              You can change this selection using the options below if needed.
            </p>
          </div>
        )}
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="vrAccessories" 
              value="Yes" 
              checked={enrollmentData.vrAccessories === 'Yes'} 
              onChange={() => updateEnrollmentData('vrAccessories', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="vrAccessories" 
              value="No" 
              checked={enrollmentData.vrAccessories === 'No'} 
              onChange={() => updateEnrollmentData('vrAccessories', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.vrAccessories && (
          <p className="mt-1 text-sm text-red-600">{errors.vrAccessories}</p>
        )}
      </div>

      {/* Receipts */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Do you have receipts for any video game related purchased? <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="hasReceipts" 
              value="Yes" 
              checked={enrollmentData.hasReceipts === 'Yes'} 
              onChange={() => updateEnrollmentData('hasReceipts', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="hasReceipts" 
              value="No" 
              checked={enrollmentData.hasReceipts === 'No'} 
              onChange={() => updateEnrollmentData('hasReceipts', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.hasReceipts && (
          <p className="mt-1 text-sm text-red-600">{errors.hasReceipts}</p>
        )}
      </div>

      {/* Monthly Spending */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          On average how much money per month was spent on video gaming? <span className="text-red-500">*</span>
        </label>
        
        {/* Auto-selection indicator - only show if value matches original auto-populated value */}
        {enrollmentData.monthlySpending && enrollmentData.monthlySpending !== '' && enrollmentData.monthlySpending === originalMonthlySpending && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Auto-populated:</span>
              <span className="ml-2 text-blue-600 font-semibold">${enrollmentData.monthlySpending}</span>
              <span className="ml-2 text-xs text-blue-600">($50-$200 range)</span>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              You can change this amount if needed.
            </p>
          </div>
        )}
        
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
          <input 
            type="number" 
            min="50"
            max="200"
            step="1"
            name="monthlySpending"
            className={`input-field w-full pl-8 ${errors.monthlySpending ? 'border-red-500' : ''} ${enrollmentData.monthlySpending && enrollmentData.monthlySpending !== '' ? 'border-blue-300 bg-blue-50' : ''}`} 
            placeholder="50-200" 
            value={enrollmentData.monthlySpending || ''} 
            onChange={(e) => {
              const value = e.target.value
              updateEnrollmentData('monthlySpending', value)
              validateMonthlySpending(value)
              // Prevent auto-selection from interfering with user changes
              if (!monthlySpendingAutoSelected) {
                setMonthlySpendingAutoSelected(true)
              }
            }} 
          />
        </div>
        {errors.monthlySpending && (
          <p className="mt-1 text-sm text-red-600">{errors.monthlySpending}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">Enter an amount between $50 and $200 (e.g., 75 for $75).</p>
      </div>
    </>
  )
}